import React from 'react';
import { Modal } from '../Shared/Modal';
import { ui } from '../../../config/uiSizes';

interface RepliCloudFAQsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

interface FaqItem {
  question: string;
  answer: React.ReactNode;
  icon: React.ReactNode;
  accent: string;
}

const faqs: FaqItem[] = [
  {
    question: 'What does a migration do?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: 'A migration applies selected Genesys Cloud objects and resources from a source environment into your target Genesys Cloud organization.'
  },
  {
    question: 'What happens during a migration?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2">When a migration runs, the following may occur:</p>
        <ul className="space-y-1.5 mb-2">
          {[
            'Selected resources are copied into the target environment',
            'Existing resources can be modified if transformations are applied during the migration process',
            'Relationships and dependencies between resources may change'
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-500 dark:text-gray-400 italic">These updates are applied directly to your target environment once the migration executes.</p>
      </>
    )
  },
  {
    question: 'Does RepliCloud create a backup before a migration?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-1">Yes. Before any migration runs, RepliCloud automatically captures a snapshot of your target organization.</p>
        <p className="text-gray-500 dark:text-gray-400 italic">This snapshot serves as a point-in-time reference of your environment before any changes are applied.</p>
      </>
    )
  },
  {
    question: 'What is a snapshot?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: 'A snapshot is a backup of your Genesys Cloud configuration at a specific moment in time, captured just before a migration executes.'
  },
  {
    question: 'What can I do with a snapshot?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2">You can download snapshot files and use them to:</p>
        <ul className="space-y-1.5">
          {[
            'Review your configuration prior to the migration',
            'Manually restore or recreate settings in Genesys Cloud if needed'
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    question: 'What are transformations?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: 'Transformations let you review and adjust how configurations will be applied to the selected resources before the migration runs, ensuring changes align with your needs before the resources are imported.'
  },
  {
    question: 'How does rollback work today?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2 font-medium text-gray-700 dark:text-gray-300">Rollback is currently a manual process.</p>
        <ul className="space-y-1.5">
          {[
            'A snapshot is automatically created before each migration',
            'If the migration results are not as expected, the snapshot provides a reliable reference for restoring your environment',
            'You must manually revert changes in Genesys Cloud using the snapshot data'
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    question: 'Who should perform a rollback?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2">Manual rollback is intended for:</p>
        <ul className="space-y-1.5">
          {['Genesys Cloud administrators', 'Developers familiar with managing configurations'].map(
            (item) => (
              <li key={item} className="flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{item}</span>
              </li>
            )
          )}
        </ul>
      </>
    )
  },
  {
    question: 'What are best practices for manual rollback?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <ul className="space-y-1.5">
        {[
          'Use the snapshot as your source of truth for restoration',
          null,
          'Additional reference can be found in ReadMe included in the downloaded migration files.'
        ].map((item, i) =>
          item === null ? (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>
                For a quick and clear understanding of what changed, click <strong>Review Target Organization Changes</strong> to access the Snapshot Comparison
                Tool, accessible from the Migration Details page. The Snapshot Comparison Tool provides an in-product comparison of pre- and post-migration snapshots
                of the Target organization.
              </span>
            </li>
          ) : item === 'Additional reference can be found in ReadMe included in the downloaded migration files.' ? (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>
                Additional reference can be found in <strong>ReadMe</strong> included in the downloaded migration files.
              </span>
            </li>
          ) : (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          )
        )}
        <li className="flex items-start gap-2">
          <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>After completing manual fixes, take a new snapshot to capture the updated state</span>
        </li>
      </ul>
    )
  },
  {
    question: 'RepliCloud Support',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <p>
        Still need help? Email support at{' '}
        <a
          href="mailto:support@edci.com"
          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          support@edci.com
        </a>
      </p>
    )
  }
];

export const RepliCloudFAQsModal: React.FC<RepliCloudFAQsModalProps> = ({
  isOpen,
  onClose,
  onBack
}) => {
  return (
    <Modal
      isOpen={isOpen}
      variant="repliCloudFAQs"
      title="RepliCloud FAQs"
      onClose={onClose}
      showCloseButton={true}
      showIcon={false}
      showConfirmButton={false}
      customButtons={
        <button
          type="button"
          onClick={onBack}
          className={`w-40 ${ui.button.modalButton} rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold shadow transition hover:bg-gray-50 dark:hover:bg-gray-600`}
        >
          ← Back
        </button>
      }
    >
      <div className="space-y-3">
        {faqs.map((faq) => (
          <div
            key={faq.question}
            className={`rounded-xl border ${faq.accent.split(' ').filter(c => c.includes('border')).join(' ')} bg-gray-50 dark:bg-gray-800/60 overflow-hidden`}
          >
            {/* Question row */}
            <div className={`flex items-center ${ui.gap.md} px-4 py-3 ${faq.accent.split(' ').filter(c => c.includes('bg')).join(' ')}`}>
              <div className={`flex-shrink-0 ${faq.accent.split(' ').filter(c => c.includes('text')).join(' ')}`}>
                {faq.icon}
              </div>
              <p className={`${ui.text.label} font-bold text-gray-900 dark:text-gray-100`}>
                {faq.question}
              </p>
            </div>
            {/* Answer row */}
            <div className={`px-4 py-3 ${ui.text.body} text-gray-700 dark:text-gray-300 leading-relaxed`}>
              {faq.answer}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
